// Shared "what should I do with this dropped file?" decision tree
// for the Watermarks Create + DCP Create source-upload flows. Both
// pages had ~25 lines of near-identical existing-file checks +
// confirm prompts; this helper consolidates them so a fix to the
// copy or the enable-flow only happens once.
//
// Returned outcomes:
//   'upload'  — caller should enqueue the file as a fresh upload
//   'skip'    — operator cancelled at a confirm prompt; do nothing
//   'enabled' — existing source got its flag flipped (caller should
//               refetch the list and NOT upload)
//
// `product` is 'watermarks' or 'dcp' — the side the operator is
// currently working on. The OTHER side is auto-derived for the
// "exists for the other product, enable here?" prompt copy.

const PRODUCT_FRIENDLY = {
  watermarks: 'Watermarks',
  dcp: 'DCP',
};

const PRODUCT_FLAG = {
  watermarks: 'available_for_watermark',
  dcp: 'available_for_dcp',
};

const PRODUCT_PATCH_KEY = {
  watermarks: 'watermarks',
  dcp: 'dcp',
};

export function otherProduct(product) {
  return product === 'watermarks' ? 'dcp' : 'watermarks';
}

// Pull a confirm() opt out for tests — caller can override.
export async function decideSourceUpload({
  file,
  existingFiles,
  product,
  api,
  confirmFn = (msg) => globalThis.confirm(msg),
}) {
  if (!file) return 'skip';
  const friendly = PRODUCT_FRIENDLY[product];
  const otherFriendly = PRODUCT_FRIENDLY[otherProduct(product)];
  const flagKey = PRODUCT_FLAG[product];
  const patchKey = PRODUCT_PATCH_KEY[product];

  const existing = (existingFiles || []).find((f) => f && f.filename === file.name);
  if (!existing) return 'upload';

  if (existing[flagKey]) {
    // Already enabled for this product — operator likely wants a
    // renamed copy. Confirm before duplicating the bytes.
    const proceed = confirmFn(
      `"${file.name}" already exists in your sources and is already available for ${friendly}. ` +
      `Click OK to upload anyway as a renamed copy, or Cancel to skip.`,
    );
    return proceed ? 'upload' : 'skip';
  }

  // Exists but flagged only for the OTHER product — offer to flip
  // the flag instead of re-uploading.
  const enable = confirmFn(
    `"${file.name}" already exists as a ${otherFriendly} source. ` +
    `Click OK to enable it for ${friendly} (no upload needed), or Cancel to upload a renamed copy.`,
  );
  if (!enable) return 'upload';

  await api.patch(
    `/api/jobs/available-files/${encodeURIComponent(file.name)}/flags`,
    { [patchKey]: true },
  );
  return 'enabled';
}
